import { Alert, Box, Button, CircularProgress, Paper, Popper, Stack, Typography } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'

const isCount = (value) => Number.isInteger(value)

/**
 * What the preview of a move says, from Sun's answer (contract 03-dominio.md §3) and nothing else: the screen
 * counts no jobs, calculates no dates, counts no days late and decides no FOs (contract §0, S150). Each line is
 * a field of the answer, or a plain reading of it:
 *   - the delivery line of the moved job reads `movedJob.newDaysLate` (late: > 0) and `currentDaysLate`
 *     (was late and is not any more) (D-F, D-T). It never uses the delivery status words of H175.
 *   - the previous-job and the "becoming late" lines only exist when Sun sends them (`null` / `count` 0 hide them).
 *   - dates are date-times read as instants and painted with the table's own format; `movedJob.requiredDate`
 *     (a day) is not painted and not converted.
 *
 * @param {Object} props
 * @param {Object} props.preview Sun's §3 answer, `{}` while there is none
 * @param {boolean} props.loading True while the preview of the typed value is on its way
 * @param {boolean} props.failed True when the preview could not be calculated (any failure but the four codes of CU9)
 */
export const ResequencePreviewContent = ({ preview = {}, loading = false, failed = false, job = {}, onConfirm = () => {}, onCancel = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()

  const ready = !loading && !failed && Object.keys(preview).length > 0
  const movedJob = preview.movedJob || {}
  const warning = preview.previousJobWarning || {}
  const becomingLate = preview.displacedBecomingLate || {}
  const fabricationOrders = Array.isArray(becomingLate.fabricationOrders) ? becomingLate.fabricationOrders : []
  const hasWarning = Boolean(preview.previousJobWarning)

  const finishesLate = isCount(movedJob.newDaysLate) && movedJob.newDaysLate > 0
  const arrivesOnTime = isCount(movedJob.newDaysLate) && movedJob.newDaysLate === 0 && isCount(movedJob.currentDaysLate) && movedJob.currentDaysLate > 0
  const heading = [movedJob.fabricationOrder || job.fabricationOrder, movedJob.job || job.job].filter(Boolean).join(' · ')

  return (
    <Stack spacing={1} sx={{ p: 2, minWidth: 320, maxWidth: 460 }} data-testid='resequence-preview'>
      {heading && <Typography variant='subtitle2'>{heading}</Typography>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={24} data-testid='resequence-preview-loading' />
        </Box>
      )}

      {failed && !loading && <Alert severity='warning'>{t_('The preview could not be calculated')}</Alert>}

      {ready && (
        <>
          <Typography variant='body2'>
            {t_('Jobs displaced: {{count}} (positions {{from}} to {{to}})', { count: preview.displacedJobCount, from: preview.displacedFirstPosition, to: preview.displacedLastPosition })}
          </Typography>
          <Typography variant='body2'>
            {t_('Jobs left with a fixed date: {{count}}', { count: preview.pinnedByMoveJobCount })}
          </Typography>
          <Typography variant='body2'>
            {t_('Current start')}: {formatDateTime(movedJob.currentStartDate)}
          </Typography>
          <Typography variant='body2'>
            {t_('New start')}: {formatDateTime(movedJob.newStartDate)}
          </Typography>
          {arrivesOnTime && <Typography variant='body2'>{t_('It would arrive on time')}</Typography>}
          {finishesLate && <Typography variant='body2' color='warning.main'>{t_('It would finish late: {{days}} days', { days: movedJob.newDaysLate })}</Typography>}
          {isCount(becomingLate.count) && becomingLate.count > 0 && (
            <Typography variant='body2' color='warning.main'>
              {t_('Displaced jobs that would finish late: {{count}}. FOs: {{fabricationOrders}}', { count: becomingLate.count, fabricationOrders: fabricationOrders.join(', ') })}
            </Typography>
          )}
          {hasWarning && (
            <Typography variant='body2' color='warning.main'>
              {t_('When replanning, it will start when the previous job ends ({{job}}, {{machine}}, {{date}})', { job: warning.job, machine: warning.deviceName, date: formatDateTime(warning.endDate) })}
            </Typography>
          )}
          <Typography variant='caption' color='text.secondary'>
            {t_('Approximate dates: they are recalculated when planning')}
          </Typography>
        </>
      )}

      <Stack direction='row' spacing={1} sx={{ justifyContent: 'flex-end', pt: 1 }}>
        <Button size='small' onClick={onCancel}>{t_('Cancel')}</Button>
        <Button size='small' variant='contained' disabled={!ready} onClick={() => onConfirm(preview.sequenceToken)}>{t_('Move')}</Button>
      </Stack>
    </Stack>
  )
}

/**
 * The preview panel, a MUI `Popper` anchored to the position field (CU4 step 3). It takes no focus and blocks
 * nothing: the field keeps focus and clicks, so the planner keeps typing and Enter stays the field's, and the table
 * stays readable for assistive technology. A `Popover` was tried first and dropped: it is a Modal, which hides the
 * rest of the page (`aria-hidden`) while it is open. "Click outside" and Esc are the field's to detect, not the panel's.
 *
 * @param {Object} props
 * @param {Element} props.anchorEl The position field; no anchor, no panel
 * @param {Object} props.preview Sun's §3 answer (`{}` while there is none)
 * @param {boolean} props.loading
 * @param {boolean} props.failed
 * @param {Object} props.job The row being moved, `{ fabricationOrder, job }` (heading while the answer is on its way)
 * @param {Function} props.onConfirm Called with the answer's `sequenceToken`, as it came (S145)
 * @param {Function} props.onCancel "Cancel" button
 */
const ResequencePreviewPanel = ({ anchorEl, onCancel = () => {}, ...content }) => (
  <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement='bottom-start' style={{ zIndex: 1300 }}>
    <Paper elevation={8} data-sequence-preview='true'>
      <ResequencePreviewContent {...content} onCancel={onCancel} />
    </Paper>
  </Popper>
)

export default ResequencePreviewPanel
